import { Information } from "iconsax-reactjs";
import { X } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";

type KybComplianceFinishReviewModalProps = {
  open: boolean;
  businessName: string;
  flaggedItemsCount: number;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

const KybComplianceFinishReviewModal = ({
  open,
  businessName,
  flaggedItemsCount,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: KybComplianceFinishReviewModalProps) => {
  const hasFlaggedItems = flaggedItemsCount > 0;
  const actionLabel = hasFlaggedItems ? "Request changes" : "Approve customer";
  const flaggedItemsLabel =
    flaggedItemsCount === 1
      ? "1 flagged item"
      : `${flaggedItemsCount} flagged items`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[640px] bg-white"
    >
      <ModalHeader className="justify-end px-6 pt-6">
        <ModalTitle className="sr-only">Finish review</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={22} />
        </button>
      </ModalHeader>

      <ModalBody className="px-8 pb-8 pt-0 text-center">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#FFF8E5]">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-[#FFE8A8]">
            <Information size={30} color="#DB6704" variant="Linear" />
          </span>
        </div>

        <h2 className="mt-6 aku-heading-h2-semibold text-[#161827]">
          Finish review?
        </h2>
        <p className="mx-auto mt-4 max-w-[520px] aku-body-large-regular text-[#383B56]">
          {hasFlaggedItems ? (
            <>
              Some information requires changes.
              <br />
              {businessName} will be notified to update and resubmit.
            </>
          ) : (
            <>
              All information and documents are approved.
              <br />
              This will mark {businessName} as verified.
            </>
          )}
        </p>

        {hasFlaggedItems ? (
          <div className="mt-8 flex items-center gap-3 rounded-[12px] bg-[#FFF8E5] px-4 py-4 text-left">
            <Information size={22} color="#DB6704" variant="Bold" />
            <p className="aku-body-normal-regular text-[#161827]">
              This customer has{" "}
              <span className="aku-body-normal-medium text-[#B33F00]">
                {flaggedItemsLabel}
              </span>{" "}
              that require changes
            </p>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter className="px-8 py-5">
        <Button
          type="button"
          variant="ghost"
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
          className="h-11 rounded-[12px] px-6 aku-body-normal-medium text-[#383B56] hover:bg-[#F7F8FD] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={isSubmitting}
          onClick={onConfirm}
          className="h-11 rounded-[12px] bg-[#161827] px-6 aku-body-normal-medium text-white hover:bg-[#2B2F49] disabled:cursor-not-allowed disabled:bg-[#9A9CC0] disabled:hover:bg-[#9A9CC0]"
        >
          {isSubmitting ? "Submitting..." : actionLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceFinishReviewModal;
